import { ayreneService } from './ayrene.service';

interface CachedMapping {
  email: string;
  ayreneUserId: string;
  timestamp: number;
}

const CACHE_KEY_PREFIX = 'ayrene_id_map_';

export const getAyreneUserId = async (
  appUserId: string,
  email: string
): Promise<string | null> => {
  console.log('🔄 Getting Ayrene user ID for:', { appUserId, email });

  const cached = getFromCache(appUserId);
  if (cached) {
    console.log('📦 Using cached Ayrene user ID:', cached);
    return cached;
  }

  try {
    const response = await ayreneService.getUserByEmail(email);

    if (response.success && response.ayreneUserId) {
      console.log('✅ Found Ayrene user ID:', response.ayreneUserId);
      cacheAyreneUserId(appUserId, email, response.ayreneUserId);
      return response.ayreneUserId;
    } else {
      console.warn('⚠️ User not found in Ayrene system:', email);
      return null;
    }
  } catch (error) {
    console.error('❌ Error fetching Ayrene user ID:', error);
    return null;
  }
};

export const cacheAyreneUserId = (
  appUserId: string,
  email: string,
  ayreneUserId: string
): void => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${appUserId}`;
    const mapping: CachedMapping = {
      email,
      ayreneUserId,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(mapping));
    console.log('💾 Cached Ayrene user ID mapping:', { appUserId, ayreneUserId });
  } catch (error) {
    console.error('❌ Error caching Ayrene user ID:', error);
  }
};

export const getFromCache = (appUserId: string): string | null => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${appUserId}`;
    const cached = localStorage.getItem(cacheKey);

    if (!cached) {
      return null;
    }

    const mapping: CachedMapping = JSON.parse(cached);
    return mapping.ayreneUserId;
  } catch (error) {
    console.error('❌ Error reading cached Ayrene user ID:', error);
    return null;
  }
};

export const clearCache = (): void => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('🗑️ Cleared all Ayrene user ID mappings from cache');
  } catch (error) {
    console.error('❌ Error clearing cache:', error);
  }
};

export const clearCacheForUser = (appUserId: string): void => {
  try {
    const cacheKey = `${CACHE_KEY_PREFIX}${appUserId}`;
    localStorage.removeItem(cacheKey);
    console.log('🗑️ Cleared Ayrene user ID mapping for:', appUserId);
  } catch (error) {
    console.error('❌ Error clearing cache for user:', error);
  }
};

export const userIdMappingService = {
  getAyreneUserId,
  cacheAyreneUserId,
  getFromCache,
  clearCache,
  clearCacheForUser
};
